import { computed, debug, IchAppAPI } from "aaa";
import { apiBaseUrl } from "config";
import { action, observable } from "mobx";
import { NetbankingAPIClient } from "network/APIClient";
import authStore from "stores/AuthStore";
import { ContactsStore } from "stores/ContactsStore";
import { IPostRegistrationResponse, IStatusResponse } from "types/ichAppServices";
import { ISigningInfo } from "types/signing";

const preferredTopics = ["SPAVO", "VERAN", "ABER"];

export class IchAppStore {
    @observable contactsStore: ContactsStore;
    @observable _isLoading = true;
    @observable postRegistrationResponse?: IPostRegistrationResponse;
    @observable status?: IStatusResponse;

    // Used for sign polling
    @observable authorizationId?: string;
    @observable authTransactionId?: string;

    @observable signingPollingUrl?: string;

    // Needed only for platforms using PdfViewer component for rendering (currently only Android)
    pdfData?: Uint8Array;

    // Agreements and download contract condition
    @observable downloadedContract = false;
    @observable downloadContractChecked = false;
    @observable readContractChecked = false;
    @observable signChecked = false;


    constructor() {
        this.contactsStore = new ContactsStore();
    }

    @computed get isLoading() {
        return this._isLoading
            || this.contactsStore.isLoading;
    }

    @action async loadData() {
        try {
            this._isLoading = true;

            await this.contactsStore.load();

            const target = IchAppAPI.getStatus();
            this.status = await NetbankingAPIClient.requestType(target);

            this._isLoading = false;
        } catch (error) {
            debug.log("[IchAppStore] loadData error:", error);

            throw error;
        }
    }

    @action resetAgreements() {
        this.downloadedContract = false;
        this.downloadContractChecked = false;
        this.readContractChecked = false;
        this.signChecked = false;
    }

    @action async postRegistration() {
        if (!this.contactsStore.selectedEmailAddress?.email?.email) {
            return;
        }

        try {
            const target = IchAppAPI.postRegistration({
                email: this.contactsStore.selectedEmailAddress.email.email,
                language: authStore.locale
            });

            this.postRegistrationResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    @action async postResendValidationCode() {
        if (!this.contactsStore.selectedEmailAddress?.email?.email || !this.postRegistrationResponse?.eId) {
            return;
        }

        try {
            const target = IchAppAPI.postResendValidationCode({
                email: this.contactsStore.selectedEmailAddress.email.email,
                language: authStore.locale,
                eId: this.postRegistrationResponse?.eId
            });

            this.postRegistrationResponse = await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    @action async putRegistration(code: string) {
        if (!this.contactsStore.selectedEmailAddress?.email?.email || !this.postRegistrationResponse?.eId) {
            return;
        }

        try {
            const target = IchAppAPI.putRegistration({
                eId: this.postRegistrationResponse?.eId,
                otp: code
            });

            return await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    @action async postAgreements() {
        try {
            const target = IchAppAPI.postAgreements();
            return await NetbankingAPIClient.requestType(target);
        } catch (error) {
            throw error;
        }
    }

    @action async postAuthorization() {
        if (!this.postRegistrationResponse?.eId) {
            return;
        }

        try {
            const target = IchAppAPI.postAuthorization(this.postRegistrationResponse?.eId);
            const res = await NetbankingAPIClient.requestType(target);
            this.authorizationId = res.authorizationId;
            this.authTransactionId = res.authTransactionId;
            this.signingPollingUrl = `${apiBaseUrl}/ichapp/rest${res._links.next.href[0] === "/" ? "" : "/"}${res._links.next.href}`;
        } catch (error) {
            throw error;
        }
    }

    @action async postDeregistration() {
        try {
            const target = IchAppAPI.postDeregistration();
            const res = await NetbankingAPIClient.requestType(target);
            this.authorizationId = res.authorizationId;
            this.authTransactionId = res.authTransactionId;
            this.signingPollingUrl = `${apiBaseUrl}/ichapp/rest${res._links.next.href[0] === "/" ? "" : "/"}${res._links.next.href}`;
        } catch (error) {
            throw error;
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/ichApp/IchAppStore.tsx